import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Car, Users, Building2, Briefcase, ShoppingBag, MessageSquareWarning,
  AlertCircle, Lightbulb, TrendingUp,
} from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import LightPageHero from '@/components/LightPageHero';

const scenarios = [
  {
    icon: Car,
    category: 'Car Service & Tyre Workshop',
    problem: 'Seasonal peaks (spring/autumn) create queues — hundreds of booking requests per day, staff work overtime and customers wait for hours.',
    solution: 'AI booking agent that responds instantly via web, chat and email. Handles booking, confirmation and reminders automatically — around the clock, without human involvement.',
    effect: 'Response time drops from hours to seconds. Staff freed up 10–15 hours/week. No missed bookings.',
    gradient: 'from-amber-400 to-orange-500',
    glow: 'rgba(251,146,60,0.25)',
    tag: 'AI Automation',
    tagColor: 'text-amber-700 bg-amber-50',
  },
  {
    icon: Users,
    category: 'Sports Association',
    problem: 'The treasurer manages manual payments, reminders and receipts via Swish. Events and kiosks also require separate solutions.',
    solution: 'Swishkassa — a simple payment solution for association treasuries, events and kiosks. Automatic receipts, real-time overview and no expensive card terminal.',
    effect: 'The treasurer saves 5–8 hours per month. Finances become transparent — everyone sees sales in real time.',
    gradient: 'from-emerald-400 to-teal-500',
    glow: 'rgba(52,211,153,0.25)',
    tag: 'Swishkassa',
    tagColor: 'text-emerald-700 bg-emerald-50',
  },
  {
    icon: Building2,
    category: 'Property Company',
    problem: 'Tenants call and email about everything — fault reports, rent questions, key replacements. The same questions are answered by staff over and over again.',
    solution: 'AI chatbot on the website that answers common questions, receives fault reports and guides tenants — without involving staff.',
    effect: 'Fewer calls to customer service — response time drops from days to seconds. Staff only handle cases requiring human judgement.',
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.25)',
    tag: 'AI Chatbot',
    tagColor: 'text-indigo-700 bg-indigo-50',
  },
  {
    icon: Briefcase,
    category: 'Consultancy / Agency',
    problem: 'Internal processes like time reporting, project reports and onboarding of new clients are done manually. It takes time and is prone to errors.',
    solution: 'Workflow automation that connects existing systems (CRM, email, spreadsheets) and automates repetitive flows — without replacing anything.',
    effect: '15–20 hours of repetitive work disappear each week. Right information in the right place automatically — no manual errors.',
    gradient: 'from-violet-500 to-purple-600',
    glow: 'rgba(139,92,246,0.25)',
    tag: 'AI Automation',
    tagColor: 'text-violet-700 bg-violet-50',
  },
  {
    icon: ShoppingBag,
    category: 'E-commerce & Order Management',
    problem: 'Order confirmations, returns and stock status updates are handled manually. Customers contact you daily to ask about status.',
    solution: 'Automated order flow: confirmations, status updates and returns handled without manual intervention. AI chatbot answers product questions 24/7.',
    effect: 'Eliminates 20+ hours of manual work per week. Customer service requests drop 40–60% — without extra staff.',
    gradient: 'from-pink-500 to-rose-500',
    glow: 'rgba(236,72,153,0.25)',
    tag: 'AI Automation',
    tagColor: 'text-pink-700 bg-pink-50',
  },
  {
    icon: MessageSquareWarning,
    category: 'Individual Consumer Case',
    problem: 'You have received an incorrect invoice, a faulty purchase or poor service — but don\'t know how to phrase a complaint that will be taken seriously.',
    solution: 'Klagobot guides you through the case and writes a professional complaint letter on your behalf — based on consumer law and best practice.',
    effect: 'You save time and get a letter that actually produces results — without any legal knowledge.',
    gradient: 'from-sky-400 to-blue-500',
    glow: 'rgba(56,189,248,0.25)',
    tag: 'Klagobot',
    tagColor: 'text-sky-700 bg-sky-50',
  },
];

const EnCustomersPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <CorporateNavbar />

      <LightPageHero
        badge="Solution Examples"
        heading={<>What can we<br /><span style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #60a5fa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>solve for you?</span></>}
        subtext="Concrete examples of how Aifinity helps businesses and organisations save time and simplify everyday operations — without long projects."
      />

      {/* ── Scenario blocks ── */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Scenarios</span>
            <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight">
              Does any of this sound familiar?
            </h2>
            <p className="text-text-secondary mt-4 max-w-xl mx-auto leading-relaxed">
              Below you'll see typical situations we solve — with a brief description of the problem, the solution and the expected impact.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {scenarios.map((s) => (
              <motion.div
                key={s.category}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="bg-white rounded-2xl border border-border-light p-7 transition-all duration-300"
                style={{ boxShadow: '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 36px ${s.glow}, 0 4px 16px rgba(0,0,0,0.05)`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)'; }}
              >
                <div className="flex items-start gap-4 mb-5">
                  <div className={`inline-flex w-11 h-11 rounded-xl bg-gradient-to-br ${s.gradient} items-center justify-center shrink-0 shadow-md`}
                    style={{ boxShadow: `0 4px 12px ${s.glow}` }}>
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full mb-1 ${s.tagColor}`}>
                      {s.tag}
                    </span>
                    <h3 className="text-[17px] font-bold text-text-primary leading-snug">{s.category}</h3>
                  </div>
                </div>

                <div className="flex gap-3 mb-3">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-red-400 mb-0.5">Problem</p>
                    <p className="text-[14px] text-text-secondary leading-relaxed">{s.problem}</p>
                  </div>
                </div>

                <div className="flex gap-3 mb-3">
                  <Lightbulb className="w-4 h-4 text-indigo-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-brand-indigo mb-0.5">Solution</p>
                    <p className="text-[14px] text-text-secondary leading-relaxed">{s.solution}</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <TrendingUp className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-600 mb-0.5">Expected Impact</p>
                    <p className="text-[14px] text-text-secondary leading-relaxed">{s.effect}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight mb-4">
              Does any of this match your situation?
            </h2>
            <p className="text-text-secondary mb-8 max-w-md mx-auto leading-relaxed">
              Tell us what takes unnecessary time at your company. There's no cost to have a conversation.
            </p>
            <Link to="/en/contact">
              <button className="group btn-glow relative overflow-hidden bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600 text-white font-semibold px-8 py-3.5 rounded-xl text-sm inline-flex items-center gap-2">
                Send a request
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default EnCustomersPage;
